import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Star, MapPin, CheckCircle, ChevronRight, Phone, Shield, Clock, ArrowUpDown, X, ChevronDown, Users, Award, Briefcase, IndianRupee, Calendar, MessageCircle, User } from 'lucide-react';
import { useAuth, api } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { formatCurrency } from '../utils/currency';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlaceholderAd } from '../components/AdSense';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getPhotoUrl = (path) => {
  if (!path) return null;
  return `${BACKEND_URL}/api/lawyers/photos/${path}`;
};

// All Indian States
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// Cities by State
const CITIES_BY_STATE = {
  'Delhi': ['New Delhi', 'Central Delhi', 'South Delhi', 'North Delhi', 'East Delhi', 'West Delhi', 'Dwarka', 'Rohini'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Navi Mumbai'],
  'Karnataka': ['Bangalore', 'Mysore', 'Mangalore', 'Hubli', 'Belgaum', 'Gulbarga', 'Davangere'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Vellore'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Secunderabad'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Darjeeling'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar'],
  'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner', 'Alwar'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Noida', 'Ghaziabad', 'Prayagraj', 'Meerut'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain', 'Sagar'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam'],
  'Punjab': ['Chandigarh', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
  'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal', 'Rohtak', 'Hisar'],
  'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga'],
  'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Brahmapur', 'Sambalpur'],
  'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Tirupati', 'Rajahmundry'],
  'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh'],
  'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Durg'],
  'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon'],
  'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rishikesh'],
  'Himachal Pradesh': ['Shimla', 'Dharamshala', 'Solan', 'Mandi', 'Kullu'],
  'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda'],
  'Jammu & Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla'],
  'Puducherry': ['Puducherry', 'Karaikal'],
  'Chandigarh': ['Chandigarh'],
};

// Popular Cities
const POPULAR_CITIES = [
  'Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad',
  'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Gurugram',
  'Noida', 'Indore', 'Bhopal', 'Patna', 'Kochi', 'Coimbatore'
];

// All Legal Categories
const LEGAL_CATEGORIES = [
  { name: 'Criminal', icon: '⚖️', desc: 'Bail, FIR, Defense, Appeals' },
  { name: 'Civil', icon: '📋', desc: 'Disputes, Recovery, Injunctions' },
  { name: 'Family', icon: '👨‍👩‍👧', desc: 'Divorce, Custody, Maintenance' },
  { name: 'Property', icon: '🏠', desc: 'Land, Real Estate, Title' },
  { name: 'Corporate', icon: '🏢', desc: 'Business, Contracts, Compliance' },
  { name: 'Tax', icon: '📊', desc: 'GST, Income Tax, TDS' },
  { name: 'Labour', icon: '👷', desc: 'Employment, PF, ESI' },
  { name: 'Consumer', icon: '🛒', desc: 'Product Issues, Refunds' },
  { name: 'Banking', icon: '🏦', desc: 'Loans, NPA, Recovery' },
  { name: 'Insurance', icon: '🛡️', desc: 'Claims, Disputes' },
  { name: 'Cyber Law', icon: '💻', desc: 'Online Fraud, IT Act' },
  { name: 'Intellectual Property', icon: '💡', desc: 'Patents, Trademarks' },
  { name: 'Immigration', icon: '✈️', desc: 'Visa, Citizenship' },
  { name: 'Motor Accident', icon: '🚗', desc: 'Claims, Compensation' },
  { name: 'Cheque Bounce', icon: '📝', desc: 'Section 138, Recovery' },
  { name: 'Documentation', icon: '📄', desc: 'Agreements, Wills, POA' },
  { name: 'Arbitration', icon: '🤝', desc: 'Mediation, ADR' },
  { name: 'Constitutional', icon: '📜', desc: 'PIL, Writs, Rights' },
];

// Budget Options
const BUDGET_OPTIONS = [
  { label: 'Under ₹500', max: 500 },
  { label: 'Under ₹1,000', max: 1000 },
  { label: 'Under ₹2,000', max: 2000 },
  { label: 'Under ₹3,000', max: 3000 },
  { label: 'Under ₹5,000', max: 5000 },
  { label: 'Under ₹7,500', max: 7500 },
  { label: 'Under ₹10,000', max: 10000 },
  { label: 'Any Budget', max: 50000 },
];

// Sort Options
const SORT_OPTIONS = [
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'experience', label: 'Most Experienced' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Search & Filter States
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBudget, setSelectedBudget] = useState(50000);
  const [sortBy, setSortBy] = useState('price_low');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllCities, setShowAllCities] = useState(false);
  
  // Lawyers
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);

  const sortLawyers = useCallback((list, sort) => {
    const sorted = [...list];
    switch (sort) {
      case 'price_low': return sorted.sort((a, b) => a.consultation_fee - b.consultation_fee);
      case 'price_high': return sorted.sort((a, b) => b.consultation_fee - a.consultation_fee);
      case 'rating': return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'experience': return sorted.sort((a, b) => b.years_of_experience - a.years_of_experience);
      default: return sorted;
    }
  }, []);

  const fetchLawyers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category', selectedCategory);
      if (selectedCity) params.set('location', selectedCity);
      if (selectedBudget < 50000) params.set('max_fee', selectedBudget);
      params.set('limit', 12);

      const res = await api.get(`/lawyers/search?${params.toString()}`);
      let fetchedLawyers = res.data.lawyers || [];
      fetchedLawyers = sortLawyers(fetchedLawyers, sortBy);
      setLawyers(fetchedLawyers);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedCategory, selectedBudget, sortBy, sortLawyers]);

  useEffect(() => {
    fetchLawyers();
  }, [fetchLawyers]);

  const clearFilters = () => {
    setSelectedState('');
    setSelectedCity('');
    setSelectedCategory('');
    setSelectedBudget(50000);
  };

  const getCitiesForState = () => {
    if (!selectedState) return [];
    return CITIES_BY_STATE[selectedState] || [];
  };

  const displayedCategories = showAllCategories ? LEGAL_CATEGORIES : LEGAL_CATEGORIES.slice(0, 9);
  const displayedCities = showAllCities ? POPULAR_CITIES : POPULAR_CITIES.slice(0, 12);

  const hasFilters = selectedCity || selectedCategory || selectedBudget < 50000;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-10">
        <div className="container-app">
          <div className="text-center max-w-2xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold"
            >
              Find Best Lawyers <span className="text-amber-500">Near You</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mt-3 text-slate-300"
            >
              500+ verified lawyers across India • Consultation starts at ₹500
            </motion.p>
            
            {/* Trust Badges */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-6 flex flex-wrap justify-center gap-4 text-sm"
            >
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-4 h-4" /> Bar Council Verified
              </span>
              <span className="flex items-center gap-1 text-emerald-400">
                <Shield className="w-4 h-4" /> Secure Payments
              </span>
              <span className="flex items-center gap-1 text-emerald-400">
                <Clock className="w-4 h-4" /> Quick Response
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ad Banner - Below Hero */}
      <div className="container-app py-4">
        <PlaceholderAd width="100%" height="90px" label="Advertisement" className="my-2" />
      </div>

      <div className="container-app py-6">
        {/* SECTION 1: Location Selection */}
        <div className="bg-white rounded-xl p-5 mb-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-amber-600" />
            <h2 className="font-semibold text-slate-900">Step 1: Select Your Location</h2>
          </div>
          
          {/* Popular Cities */}
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-2 font-medium">POPULAR CITIES</p>
            <div className="flex flex-wrap gap-2">
              {displayedCities.map(city => (
                <button
                  key={city}
                  onClick={() => { setSelectedCity(city); setSelectedState(''); }}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    selectedCity === city
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-amber-100'
                  }`}
                  data-testid={`city-${city.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {city}
                </button>
              ))}
              <button
                onClick={() => setShowAllCities(!showAllCities)}
                className="px-3 py-1.5 text-amber-600 text-sm font-medium hover:underline"
              >
                {showAllCities ? 'Show Less' : `+${POPULAR_CITIES.length - 12} More`}
              </button>
            </div>
          </div>

          {/* State & City Dropdowns */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-500 mb-1 font-medium">SELECT STATE</p>
              <Select value={selectedState} onValueChange={(val) => { setSelectedState(val); setSelectedCity(''); }}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Choose State" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1 font-medium">SELECT CITY</p>
              {selectedState && getCitiesForState().length > 0 ? (
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder="Choose City" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {getCitiesForState().map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="text"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  placeholder="Type city name..."
                  data-testid="city-input"
                />
              )}
            </div>
          </div>
        </div>

        {/* SECTION 2: Legal Category */}
        <div className="bg-white rounded-xl p-5 mb-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold text-slate-900">Step 2: Select Legal Category</h2>
            </div>
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="text-sm text-amber-600 font-medium flex items-center gap-1"
            >
              {showAllCategories ? 'Show Less' : `View All (${LEGAL_CATEGORIES.length})`}
              <ChevronDown className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2">
            {displayedCategories.map(cat => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(selectedCategory === cat.name ? '' : cat.name)}
                className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                  selectedCategory === cat.name
                    ? 'bg-amber-50 border-2 border-amber-500 shadow-sm'
                    : 'bg-slate-50 border border-slate-200 hover:border-amber-300 hover:bg-amber-50'
                }`}
                data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="text-2xl">{cat.icon}</span>
                <span className="text-xs mt-1 text-center text-slate-700 font-medium line-clamp-1">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* SECTION 3: Budget Selection */}
        <div className="bg-white rounded-xl p-5 mb-4 border border-slate-200 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <IndianRupee className="w-5 h-5 text-amber-600" />
                <h2 className="font-semibold text-slate-900">Step 3: Set Your Budget (per consultation)</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {BUDGET_OPTIONS.map((budget) => (
                  <button
                    key={budget.max}
                    onClick={() => setSelectedBudget(budget.max)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedBudget === budget.max
                        ? 'bg-slate-900 text-white shadow-md'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`budget-${budget.max}`}
                  >
                    {budget.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="lg:w-48">
              <p className="text-xs text-slate-500 mb-1 font-medium">SORT BY</p>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Active Filters */}
        {hasFilters && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-sm text-slate-500">Active Filters:</span>
            {selectedCity && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {selectedCity}
                <button onClick={() => setSelectedCity('')}><X className="w-3 h-3" /></button>
              </span>
            )}
            {selectedCategory && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm flex items-center gap-1">
                {selectedCategory}
                <button onClick={() => setSelectedCategory('')}><X className="w-3 h-3" /></button>
              </span>
            )}
            {selectedBudget < 50000 && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm flex items-center gap-1">
                Budget: {formatCurrency(selectedBudget)}
                <button onClick={() => setSelectedBudget(50000)}><X className="w-3 h-3" /></button>
              </span>
            )}
            <button onClick={clearFilters} className="text-sm text-red-600 hover:underline font-medium">
              Clear All
            </button>
          </div>
        )}

        {/* SECTION 4: Lawyer Results */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold text-slate-900">
                {loading ? 'Searching...' : `Found ${lawyers.length} Lawyer${lawyers.length !== 1 ? 's' : ''}`}
                {selectedCity && ` in ${selectedCity}`}
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-slate-50 rounded-xl p-5 animate-pulse">
                  <div className="flex gap-3">
                    <div className="w-14 h-14 bg-slate-200 rounded-xl" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : lawyers.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">No lawyers found</h3>
              <p className="text-slate-500 mt-2">Try changing your location, category or budget</p>
              <Button onClick={clearFilters} className="mt-4">Clear All Filters</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lawyers.map((lawyer, idx) => (
                <motion.div
                  key={lawyer.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="bg-slate-50 rounded-xl p-5 border border-slate-200 hover:shadow-lg hover:border-amber-300 transition-all"
                  data-testid={`lawyer-${idx}`}
                >
                  {/* Top Rated Badge */}
                  {lawyer.rating >= 4 && (
                    <div className="mb-2">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                        ⭐ Top Rated
                      </span>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center text-xl font-bold text-amber-700 shrink-0 overflow-hidden">
                      {lawyer.profile_photo ? (
                        <img
                          src={getPhotoUrl(lawyer.profile_photo)}
                          alt={lawyer.user?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        lawyer.user?.name?.charAt(0) || 'L'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{lawyer.user?.name}</h3>
                      <p className="text-sm text-slate-500">{lawyer.qualification}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          <span className="text-sm font-medium">{lawyer.rating || 0}</span>
                        </div>
                        <span className="text-slate-300">•</span>
                        <span className="text-sm text-slate-500">{lawyer.years_of_experience} yrs exp</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span className="truncate">{lawyer.user?.location || 'India'}</span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1">
                    {lawyer.specializations?.slice(0, 2).map(spec => (
                      <span key={spec} className="px-2 py-0.5 bg-white text-slate-600 rounded text-xs border">
                        {spec}
                      </span>
                    ))}
                  </div>

                  {/* Languages */}
                  {lawyer.languages?.length > 0 && (
                    <p className="mt-2 text-xs text-slate-500">
                      🗣️ {lawyer.languages.slice(0, 3).join(', ')}
                    </p>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Consultation Fee</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(lawyer.consultation_fee)}</p>
                    </div>
                    <Link
                      to={`/lawyers/${lawyer.id}`}
                      className="px-4 py-2 bg-amber-600 text-white rounded-full text-sm font-medium hover:bg-amber-700 transition-colors"
                      data-testid={`book-${idx}`}
                    >
                      Book Now
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {lawyers.length > 0 && (
            <div className="mt-6 text-center">
              <Link
                to="/lawyers"
                className="inline-flex items-center gap-1 text-amber-600 font-medium hover:underline"
              >
                View All Lawyers <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>

        {/* SECTION 5: For Lawyers */}
        <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-bold mb-4">Are You a Lawyer?</h2>
              <p className="text-slate-300 mb-6">
                Join 500+ lawyers on LegalHub. Get verified clients, manage appointments, and grow your practice.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span>Free registration & profile creation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span>Get verified client leads</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span>Set your own consultation fees</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span>Manage appointments & earnings</span>
                </div>
              </div>

              <Link
                to="/register/lawyer"
                className="inline-block px-6 py-3 bg-amber-600 hover:bg-amber-700 rounded-full font-medium transition-colors"
                data-testid="register-lawyer-btn"
              >
                Register as Lawyer →
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">500+</p>
                <p className="text-sm text-slate-300">Verified Lawyers</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">10K+</p>
                <p className="text-sm text-slate-300">Consultations</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">33</p>
                <p className="text-sm text-slate-300">States Covered</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">18</p>
                <p className="text-sm text-slate-300">Legal Categories</p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-8 bg-white rounded-xl p-6 border border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 text-center mb-6">How It Works</h2>
          
          {/* Ad Banner - Before How It Works */}
          <div className="mb-6">
            <PlaceholderAd width="100%" height="90px" label="Advertisement" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Clients */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" /> For Clients
              </h3>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Search', desc: 'Find lawyers by city, category & budget' },
                  { step: '2', title: 'Compare', desc: 'Check ratings, experience & fees' },
                  { step: '3', title: 'Book', desc: 'Select time slot & consultation mode' },
                  { step: '4', title: 'Pay', desc: 'Secure payment via UPI, Card or Net Banking' },
                ].map(item => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-sm shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.title}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* For Lawyers */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-600" /> For Lawyers
              </h3>
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Register', desc: 'Create profile with Bar Council details' },
                  { step: '2', title: 'Get Verified', desc: 'Admin approves your profile' },
                  { step: '3', title: 'Set Availability', desc: 'Define time slots & consultation fee' },
                  { step: '4', title: 'Get Clients', desc: 'Accept bookings & grow your practice' },
                ].map(item => (
                  <div key={item.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.title}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 py-4 border-t border-amber-200 mt-8">
        <div className="container-app">
          <p className="text-xs text-amber-800 text-center">
            <strong>Disclaimer:</strong> LegalHub connects clients with independent lawyers. We are not a law firm and do not guarantee outcomes.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage;
